// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";

/**
 * Redux's typings include a `Dispatch` helper type, but it doesn't alert if the dispatched action has extra properties.
 * This solution isn't perfect because it doesn't return the provided action type, but its stricter input checking is more important.
 */
type StrictInputDispatch = (
    action: AnyAction | ((dispatch: StrictInputDispatch, getState: () => ApplicationState) => void),
) => AnyAction;

type Thunk<Result> = (dispatch: StrictInputDispatch, getState: () => ApplicationState) => Result;

// Custom Awaitable function for handlers with no OnSuccess property in order to resolve onComplete
export const makeHandlerChainAwaitableCustom =
    <Parameter, Result>(handlerChain: (parameter: Parameter) => Thunk<void>) =>
    (parameter: Omit<Parameter, "onSuccess">): Thunk<Promise<Result>> =>
    dispatch =>
        new Promise<Result>((resolve, reject) => {
            dispatch(
                handlerChain({
                    ...(parameter as Parameter),
                    onComplete: resolve,
                    onException: reject,
                }),
            );
        });
